import React, { useState } from 'react';
import { X, Save, Eye, Calendar, Download } from 'lucide-react';

export default function CustomReportBuilder({ availableMetrics, savedReports, onSave, onClose }) {
  const [reportName, setReportName] = useState('');
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [reportType, setReportType] = useState('summary');
  const [exportFormat, setExportFormat] = useState('pdf');
  const [schedule, setSchedule] = useState('none');
  const [showSavedReports, setShowSavedReports] = useState(false);

  const handleMetricToggle = (metric) => {
    if (selectedMetrics?.includes(metric)) {
      setSelectedMetrics(selectedMetrics?.filter(m => m !== metric));
    } else {
      setSelectedMetrics([...selectedMetrics, metric]);
    }
  };

  const handleSave = () => {
    const reportConfig = {
      name: reportName,
      metrics: selectedMetrics,
      type: reportType,
      exportFormat,
      schedule,
      createdAt: new Date()?.toISOString()
    };
    onSave(reportConfig);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Custom Report Builder</h2>
            <p className="text-sm text-gray-600 mt-1">Create and schedule custom analytics reports</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-auto max-h-[calc(90vh-180px)]">
          {/* Saved Reports View */}
          {showSavedReports ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Saved Reports</h3>
                <button
                  onClick={() => setShowSavedReports(false)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Create New
                </button>
              </div>
              {savedReports?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No saved reports yet</p>
              ) : (
                savedReports?.map((report, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{report?.name}</h4>
                        <p className="text-sm text-gray-500 mt-1">
                          {report?.metrics?.length} metrics • {report?.type} • {report?.schedule}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button className="p-2 hover:bg-gray-100 rounded-lg">
                          <Eye size={18} />
                        </button>
                        <button className="p-2 hover:bg-gray-100 rounded-lg">
                          <Download size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Report Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Name *
                </label>
                <input
                  type="text"
                  value={reportName}
                  onChange={(e) => setReportName(e?.target?.value)}
                  placeholder="e.g., Monthly Performance Report"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Select Metrics */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Metrics *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {availableMetrics?.map(metric => (
                    <button
                      key={metric}
                      onClick={() => handleMetricToggle(metric)}
                      className={`p-3 text-left border rounded-lg transition-colors ${
                        selectedMetrics?.includes(metric)
                          ? 'bg-blue-50 border-blue-500 text-blue-900' :'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-sm font-medium">
                        {metric?.replace(/_/g, ' ')?.split(' ')?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))?.join(' ')}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Report Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Type
                </label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="summary">Summary Overview</option>
                  <option value="detailed">Detailed Analysis</option>
                  <option value="comparison">Period Comparison</option>
                  <option value="trend">Trend Analysis</option>
                </select>
              </div>

              {/* Export Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Export Format
                </label>
                <div className="flex gap-3">
                  {['pdf', 'excel', 'csv']?.map(format => (
                    <button
                      key={format}
                      onClick={() => setExportFormat(format)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        exportFormat === format
                          ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {format?.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Schedule */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar size={16} className="inline mr-1" />
                  Schedule Report
                </label>
                <select
                  value={schedule}
                  onChange={(e) => setSchedule(e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="none">One-time only</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>

              {/* Saved Reports Link */}
              <button
                onClick={() => setShowSavedReports(true)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View {savedReports?.length} saved report{savedReports?.length !== 1 ? 's' : ''}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {!showSavedReports && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!reportName || selectedMetrics?.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={20} />
              <span>Save Report Template</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}